import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import EkgLine from "@/components/EkgLine";
import NinjaBadge from "@/components/NinjaBadge";
import { Lock, CheckCircle2, Play, Trophy, Flame, Skull, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const { user, rank } = useAuth();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/levels").then(({ data }) => {
      setLevels(data.levels);
      setLoading(false);
    });
  }, []);

  const completed = user?.completed_levels || [];
  const highestCompleted = completed.length ? Math.max(...completed) : 0;
  const maxUnlocked = highestCompleted + 1;
  const overallProgress = Math.round((completed.length / 8) * 100);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      {/* Hero stat panel */}
      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 bg-ekg-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="reveal p-6 rounded-lg border border-green-500/30 bg-green-500/5 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <NinjaBadge
                  level={Math.max(1, highestCompleted || 1)}
                  size={96}
                  glow
                />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">
                    Current Rank
                  </div>
                  <div className="font-display text-xl font-bold" data-testid="dashboard-rank">
                    {rank || "Student Ninja"}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Welcome back, {user?.name || "ninja"}.
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal p-6 rounded-lg border border-white/10 bg-white/[0.02]">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Total XP
              </div>
              <div className="font-display text-4xl font-bold" data-testid="dashboard-xp">
                {user?.xp || 0}
              </div>
              <div className="text-sm text-slate-400 mt-2 flex items-center gap-1">
                <Flame className="w-3 h-3 text-red-400" /> {completed.length} of 8 belts
              </div>
            </div>

            <div className="reveal p-6 rounded-lg border border-white/10 bg-white/[0.02]">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Journey Progress
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold">{overallProgress}</span>
                <span className="text-slate-400">%</span>
              </div>
              <Progress
                value={overallProgress}
                className="mt-3 h-1.5 bg-white/10"
                data-testid="dashboard-progress"
              />
            </div>
          </div>

          <div className="mt-10 h-20 opacity-60">
            <EkgLine className="w-full h-full" />
          </div>
        </div>
      </section>

      {/* Code Blue arcade CTA */}
      <section className="max-w-7xl mx-auto px-6 pt-10">
        <Link
          to="/code-blue"
          className="reveal group relative block overflow-hidden rounded-lg border border-red-500/30 bg-red-500/5 hover:border-red-500/60 hover:bg-red-500/10 transition-colors p-6 sm:p-8"
          data-testid="code-blue-cta"
        >
          <div className="relative z-10 flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-start gap-4 max-w-xl">
              <div className="w-12 h-12 flex-none rounded-md bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                <Skull className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-red-400 flex items-center gap-2 mb-1">
                  <Zap className="w-3 h-3" /> Arcade Mode
                </div>
                <h3 className="font-display text-2xl font-bold">Code Blue Rhythm Rescue</h3>
                <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                  Random rhythms flash on the monitor. You have seconds to identify each. Miss one — game over. Chase the top of the leaderboard.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Your High</div>
                <div className="font-display text-3xl font-bold text-red-400" data-testid="dashboard-code-blue-high">
                  {user?.code_blue_high_score || 0}
                </div>
              </div>
              <div className="rounded-full bg-red-500 group-hover:bg-red-400 text-white font-semibold px-6 py-3 text-sm flex items-center gap-2 transition-colors">
                Enter
                <Play className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* Levels grid */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">
              The Path
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              Choose your training
            </h2>
          </div>
          <div className="text-sm text-slate-400 hidden sm:block">
            {highestCompleted}/8 belts earned
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500 font-mono text-sm">Loading levels…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {levels.map((lvl, idx) => {
              const isCompleted = completed.includes(lvl.id);
              const isCurrent = lvl.id === maxUnlocked && !isCompleted;
              const isLocked = lvl.id > maxUnlocked;
              const score = user?.quiz_scores?.[lvl.id];

              return (
                <div
                  key={lvl.id}
                  className={`reveal group relative rounded-lg border p-6 flex flex-col justify-between min-h-[280px] transition-colors ${
                    isLocked
                      ? "border-white/5 bg-white/[0.015] opacity-60"
                      : isCompleted
                      ? "border-green-500/40 bg-green-500/5 hover:border-green-500/60"
                      : isCurrent
                      ? "border-red-500/40 bg-red-500/5 hover:border-red-500/70 glow-red"
                      : "border-white/10 bg-white/[0.03] hover:border-green-500/40"
                  }`}
                  style={{ animationDelay: `${idx * 40}ms` }}
                  data-testid={`level-card-${lvl.id}`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-slate-500">
                        LVL {String(lvl.id).padStart(2, "0")}
                      </span>
                      {isCompleted && (
                        <CheckCircle2 className="w-4 h-4 text-green-400" data-testid={`level-completed-${lvl.id}`} />
                      )}
                      {isLocked && <Lock className="w-4 h-4 text-slate-600" />}
                      {isCurrent && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-red-400 px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/10">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-start justify-between mt-3 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg font-semibold leading-tight" data-testid={`level-rank-${lvl.id}`}>
                          {lvl.rank}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{lvl.tagline}</div>
                      </div>
                      <div className={`flex-none transition-transform group-hover:scale-110 ${isLocked ? "grayscale opacity-60" : ""}`}>
                        <NinjaBadge level={lvl.id} size={72} glow={isCurrent || isCompleted} locked={isLocked} />
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed line-clamp-2">
                      {lvl.description}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 text-slate-500">
                      <span>{lvl.lesson_count} lessons</span>
                      <span>·</span>
                      <span>{lvl.quiz_count}Q quiz</span>
                    </div>
                    {isLocked ? (
                      <span className="font-mono text-slate-600">LOCKED</span>
                    ) : (
                      <Link
                        to={`/level/${lvl.id}`}
                        className={`font-mono text-xs uppercase tracking-widest flex items-center gap-1 ${
                          isCompleted ? "text-green-400" : "text-white group-hover:text-green-400"
                        }`}
                        data-testid={`level-enter-${lvl.id}`}
                      >
                        {isCompleted ? (
                          <>
                            Review {score ? `· ${score}%` : ""}
                            <Play className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            Train
                            <Play className="w-3 h-3" />
                          </>
                        )}
                      </Link>
                    )}
                  </div>

                  <div className="absolute bottom-4 left-6 text-[10px] font-mono text-green-400 opacity-40">
                    +{lvl.xp}xp
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
